<template>
  <a-modal
    v-model:open="visible"
    title="全局搜索"
    :footer="null"
    width="700px"
    @cancel="handleClose"
  >
    <div class="global-search">
      <a-input-search
        ref="searchInput"
        v-model:value="searchKeyword"
        placeholder="搜索工单、用户、资源..."
        size="large"
        @search="handleSearch"
        @input="handleInput"
        allow-clear
      >
        <template #prefix>
          <SearchOutlined />
        </template>
      </a-input-search>

      <div class="search-tips" v-if="!searchKeyword">
        <a-space direction="vertical" style="width: 100%">
          <div class="tip-item">
            <kbd>Ctrl</kbd> + <kbd>K</kbd> 或 <kbd>Cmd</kbd> + <kbd>K</kbd> 打开搜索
          </div>
          <div class="tip-item">
            <kbd>ESC</kbd> 关闭搜索
          </div>
          <a-divider style="margin: 12px 0" />
          <div class="quick-links">
            <div class="quick-link-title">快速访问</div>
            <a-space wrap>
              <a-tag @click="navigateTo('/ticket/tickets')">工单管理</a-tag>
              <a-tag @click="navigateTo('/k8s/workload')">工作负载</a-tag>
              <a-tag @click="navigateTo('/admin/user')">用户管理</a-tag>
              <a-tag @click="navigateTo('/audit')">操作审计</a-tag>
            </a-space>
          </div>
        </a-space>
      </div>

      <div class="search-results" v-else-if="searchKeyword && !loading">
        <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
          <a-tab-pane key="all" tab="全部">
            <a-list
              :data-source="allResults"
              :loading="loading"
            >
              <template #renderItem="{ item }">
                <a-list-item @click="handleResultClick(item)" class="result-item">
                  <a-list-item-meta>
                    <template #avatar>
                      <a-avatar :style="{ backgroundColor: item.color }">
                        <template #icon>
                          <component :is="item.icon" />
                        </template>
                      </a-avatar>
                    </template>
                    <template #title>
                      <a-space>
                        <span>{{ item.title }}</span>
                        <a-tag size="small" :color="item.typeColor">{{ item.type }}</a-tag>
                      </a-space>
                    </template>
                    <template #description>
                      {{ item.description }}
                    </template>
                  </a-list-item-meta>
                </a-list-item>
              </template>
              <template #loadMore v-if="allResults.length === 0">
                <a-empty description="未找到相关结果" />
              </template>
            </a-list>
          </a-tab-pane>

          <a-tab-pane key="tickets" :tab="`工单 (${ticketResults.length})`">
            <a-list :data-source="ticketResults" :loading="loading">
              <template #renderItem="{ item }">
                <a-list-item @click="handleResultClick(item)" class="result-item">
                  <a-list-item-meta>
                    <template #title>
                      <a-space>
                        <span>{{ item.title }}</span>
                        <a-tag :color="item.statusColor">{{ item.status }}</a-tag>
                      </a-space>
                    </template>
                    <template #description>
                      {{ item.description }}
                    </template>
                  </a-list-item-meta>
                </a-list-item>
              </template>
            </a-list>
          </a-tab-pane>

          <a-tab-pane key="users" :tab="`用户 (${userResults.length})`">
            <a-list :data-source="userResults" :loading="loading">
              <template #renderItem="{ item }">
                <a-list-item @click="handleResultClick(item)" class="result-item">
                  <a-list-item-meta>
                    <template #avatar>
                      <a-avatar>{{ item.title.charAt(0) }}</a-avatar>
                    </template>
                    <template #title>
                      {{ item.title }}
                    </template>
                    <template #description>
                      {{ item.description }}
                    </template>
                  </a-list-item-meta>
                </a-list-item>
              </template>
            </a-list>
          </a-tab-pane>

          <a-tab-pane key="resources" :tab="`资源 (${resourceResults.length})`">
            <a-list :data-source="resourceResults" :loading="loading">
              <template #renderItem="{ item }">
                <a-list-item @click="handleResultClick(item)" class="result-item">
                  <a-list-item-meta>
                    <template #title>
                      {{ item.title }}
                    </template>
                    <template #description>
                      {{ item.description }}
                    </template>
                  </a-list-item-meta>
                </a-list-item>
              </template>
            </a-list>
          </a-tab-pane>
        </a-tabs>
      </div>

      <a-spin v-else-if="loading" style="display: block; text-align: center; padding: 40px;" />
    </div>
  </a-modal>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import {
  SearchOutlined,
  FileTextOutlined,
  UserOutlined,
  CloudOutlined,
} from '@ant-design/icons-vue';
import { searchGlobal } from '@/api/search';

const router = useRouter();
const emit = defineEmits(['close']);

// 状态
const visible = ref(false);
const searchKeyword = ref('');
const loading = ref(false);
const activeTab = ref('all');
const searchInput = ref(null);

// 搜索结果
const ticketResults = ref([]);
const userResults = ref([]);
const resourceResults = ref([]);

// 全部结果
const allResults = computed(() => {
  return [...ticketResults.value, ...userResults.value, ...resourceResults.value];
});

// 打开搜索
const open = () => {
  visible.value = true;
  nextTick(() => {
    searchInput.value?.focus();
  });
};

// 关闭搜索
const handleClose = () => {
  visible.value = false;
  searchKeyword.value = '';
  ticketResults.value = [];
  userResults.value = [];
  resourceResults.value = [];
  activeTab.value = 'all';
  emit('close');
};

// 输入处理（防抖）
let searchTimer = null;
const handleInput = () => {
  if (searchTimer) clearTimeout(searchTimer);
  
  if (!searchKeyword.value.trim()) {
    ticketResults.value = [];
    userResults.value = [];
    resourceResults.value = [];
    return;
  }
  
  searchTimer = setTimeout(() => {
    handleSearch();
  }, 300);
};

// 执行搜索
const handleSearch = async () => {
  if (!searchKeyword.value.trim()) return;
  
  loading.value = true;
  try {
    const response = await searchGlobal({ keyword: searchKeyword.value });
    const data = response.data || {};
    
    // 处理工单结果
    ticketResults.value = (data.tickets || []).map(item => ({
      id: item.id,
      title: item.title,
      description: item.description || `ID: ${item.id}`,
      type: '工单',
      typeColor: 'blue',
      icon: FileTextOutlined,
      color: '#1890ff',
      status: item.status,
      statusColor: getStatusColor(item.status),
      path: `/ticket/tickets?id=${item.id}`,
    }));
    
    // 处理用户结果
    userResults.value = (data.users || []).map(item => ({
      id: item.id,
      title: item.username,
      description: item.email || item.full_name || '-',
      type: '用户',
      typeColor: 'green',
      icon: UserOutlined,
      color: '#52c41a',
      path: `/admin/user?id=${item.id}`,
    }));
    
    // 处理资源结果
    resourceResults.value = (data.resources || []).map(item => ({
      id: item.id,
      title: item.name,
      description: `${item.type} - ${item.cluster || '-'}`,
      type: '资源',
      typeColor: 'purple',
      icon: CloudOutlined,
      color: '#722ed1',
      path: item.path || '/k8s/workload',
    }));
  } catch (error) {
    console.error('搜索失败:', error);
  } finally {
    loading.value = false;
  }
};

// 切换标签
const handleTabChange = (key) => {
  activeTab.value = key;
};

// 点击结果
const handleResultClick = (item) => {
  if (item.path) {
    router.push(item.path);
    handleClose();
  }
};

// 快速导航
const navigateTo = (path) => {
  router.push(path);
  handleClose();
};

// 工具函数
const getStatusColor = (status) => {
  const map = {
    'pending': 'orange',
    'in_progress': 'blue',
    'approved': 'green',
    'rejected': 'red',
    'completed': 'cyan',
    'cancelled': 'default',
  };
  return map[status] || 'default';
};

// 暴露方法
defineExpose({
  open,
  close: handleClose,
});
</script>

<style scoped>
.global-search {
  min-height: 300px;
}

.search-tips {
  margin-top: 20px;
  padding: 16px;
  background: #fafafa;
  border-radius: 4px;
}

.tip-item {
  color: #666;
  font-size: 13px;
}

.tip-item kbd {
  display: inline-block;
  padding: 2px 6px;
  font-size: 12px;
  line-height: 1.4;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
  margin: 0 2px;
}

.quick-links {
  margin-top: 8px;
}

.quick-link-title {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.quick-links :deep(.ant-tag) {
  cursor: pointer;
  margin-bottom: 8px;
}

.quick-links :deep(.ant-tag:hover) {
  opacity: 0.8;
}

.search-results {
  margin-top: 16px;
  max-height: 500px;
  overflow-y: auto;
}

.result-item {
  cursor: pointer;
  transition: background 0.3s;
}

.result-item:hover {
  background: #fafafa;
}

:deep(.ant-list-item) {
  padding: 12px 16px;
}
</style>
